import React, { useState, useEffect, useRef } from 'react';
import { ApiClient } from '../../api/client';
import {
  LifeBuoy, Plus, Search, Filter, MessageSquare, Clock,
  ShieldAlert, Send, ChevronRight, RefreshCw, X, Paperclip,
  Image as ImageIcon, FileText, Download, Eye, Trash2
} from 'lucide-react';

export interface TicketAttachment {
  name: string;
  key: string;
  size: number;
  type: string;
  url?: string;
}

interface TicketItem {
  id: number;
  tenant_id: number;
  user_id?: number;
  user_name?: string;
  user_email?: string;
  title: string;
  description: string;
  category: 'bug' | 'feature' | 'compliance' | 'data' | 'security_nis2' | 'other';
  priority: 'low' | 'normal' | 'high' | 'urgent';
  status: 'open' | 'in_progress' | 'waiting_user' | 'resolved' | 'closed';
  record_id?: number;
  page_id?: number;
  attachments?: TicketAttachment[] | string;
  created_at: string;
  updated_at: string;
  comment_count?: number;
}

interface TicketComment {
  id: number;
  ticket_id: number;
  user_id?: number;
  user_name?: string;
  user_role?: string;
  comment: string;
  attachments?: TicketAttachment[] | string;
  is_internal?: number;
  created_at: string;
}

const formatBytes = (bytes: number): string => {
  if (!bytes || bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
};

const parseAttachments = (att: any): TicketAttachment[] => {
  if (!att) return [];
  let list: any[] = [];
  if (Array.isArray(att)) list = att;
  else if (typeof att === 'string') {
    try {
      const parsed = JSON.parse(att);
      list = Array.isArray(parsed) ? parsed : [];
    } catch {
      list = [];
    }
  }
  return list.map(item => ({
    name: item.name || item.filename || 'melléklet',
    key: item.key || item.r2_key || '',
    size: item.size || 0,
    type: item.type || item.mime_type || 'application/octet-stream',
    url: item.url || (item.key || item.r2_key ? `/api/files/${encodeURIComponent(item.key || item.r2_key)}` : undefined),
  }));
};

export const TicketsView: React.FC = () => {
  const [tickets, setTickets] = useState<TicketItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [priorityFilter, setPriorityFilter] = useState('all');

  // New Ticket Modal State
  const [isNewModalOpen, setIsNewModalOpen] = useState(false);
  const [newTitle, setNewTitle] = useState('');
  const [newDesc, setNewDesc] = useState('');
  const [newCategory, setNewCategory] = useState<TicketItem['category']>('bug');
  const [newPriority, setNewPriority] = useState<TicketItem['priority']>('normal');
  const [newAttachments, setNewAttachments] = useState<TicketAttachment[]>([]);
  const [uploadingAttachment, setUploadingAttachment] = useState(false);
  const [creating, setCreating] = useState(false);
  const newFileInputRef = useRef<HTMLInputElement>(null);

  // Detail Modal / Drawer State
  const [selectedTicket, setSelectedTicket] = useState<TicketItem | null>(null);
  const [comments, setComments] = useState<TicketComment[]>([]);
  const [newComment, setNewComment] = useState('');
  const [commentAttachments, setCommentAttachments] = useState<TicketAttachment[]>([]);
  const [uploadingCommentAttachment, setUploadingCommentAttachment] = useState(false);
  const [sendingComment, setSendingComment] = useState(false);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const commentFileInputRef = useRef<HTMLInputElement>(null);

  // Lightbox Preview Image State
  const [previewImage, setPreviewImage] = useState<{ url: string; name: string } | null>(null);

  const fetchTickets = async () => {
    setLoading(true);
    try {
      const res = await ApiClient.getTickets({
        status: statusFilter !== 'all' ? statusFilter : undefined,
        priority: priorityFilter !== 'all' ? priorityFilter : undefined,
      });
      if (res.ok && res.data) {
        setTickets(res.data);
      }
    } catch (e) {
      console.error('Hiba a jegyek betöltésekor:', e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTickets();
  }, [statusFilter, priorityFilter]);

  const handleOpenDetail = async (ticket: TicketItem) => {
    setSelectedTicket(ticket);
    setComments([]);
    setCommentAttachments([]);
    setNewComment('');
    setLoadingDetail(true);
    try {
      const res = await ApiClient.getTicket(ticket.id);
      if (res.ok && res.data) {
        setSelectedTicket(res.data.ticket || ticket);
        setComments(res.data.comments || []);
      }
    } catch (e) {
      console.error('Hiba a jegy részleteinek lekérésekor:', e);
    } finally {
      setLoadingDetail(false);
    }
  };

  // Upload helper for generic file array
  const uploadFiles = async (
    files: File[],
    setUploading: (v: boolean) => void,
    onSuccess: (uploaded: TicketAttachment) => void
  ) => {
    if (!files || files.length === 0) return;
    setUploading(true);
    try {
      for (const file of files) {
        if (file.size > 15 * 1024 * 1024) {
          alert(`A(z) ${file.name} fájl mérete meghaladja a 15 MB-os limitet!`);
          continue;
        }
        const res = await ApiClient.uploadTicketAttachment(file, file.name);
        if (res.ok && res.data) {
          onSuccess(res.data);
        } else {
          alert(`Hiba a(z) ${file.name} feltöltésekor: ${res.error || 'Ismeretlen hiba'}`);
        }
      }
    } catch (err: any) {
      alert('Feltöltési hiba: ' + err.message);
    } finally {
      setUploading(false);
    }
  };

  // Handle Clipboard Paste (e.g. screenshots with Ctrl+V)
  const handlePasteCapture = (
    e: React.ClipboardEvent,
    setUploading: (v: boolean) => void,
    onSuccess: (uploaded: TicketAttachment) => void
  ) => {
    const items = e.clipboardData?.items;
    if (!items) return;

    const filesToUpload: File[] = [];
    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      if (item.type && item.type.startsWith('image/')) {
        const file = item.getAsFile();
        if (file) {
          const timestamp = new Date().toISOString().replace(/[-:T.]/g, '').slice(0, 14);
          const ext = file.type.split('/')[1] || 'png';
          const renamed = new File([file], `kepernyokep_${timestamp}.${ext}`, { type: file.type });
          filesToUpload.push(renamed);
        }
      }
    }

    if (filesToUpload.length > 0) {
      e.preventDefault();
      uploadFiles(filesToUpload, setUploading, onSuccess);
    }
  };

  const handleCreateTicket = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newTitle.trim() || !newDesc.trim()) return;
    setCreating(true);
    try {
      const res = await ApiClient.createTicket({
        title: newTitle.trim(),
        description: newDesc.trim(),
        category: newCategory,
        priority: newPriority,
        attachments: newAttachments,
      });
      if (res.ok) {
        setIsNewModalOpen(false);
        setNewTitle('');
        setNewDesc('');
        setNewCategory('bug');
        setNewPriority('normal');
        setNewAttachments([]);
        fetchTickets();
      } else {
        alert(res.error || 'Hiba a hibajegy mentésekor');
      }
    } catch (e: any) {
      alert('Hiba: ' + e.message);
    } finally {
      setCreating(false);
    }
  };

  const handleAddComment = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedTicket || (!newComment.trim() && commentAttachments.length === 0)) return;
    setSendingComment(true);
    try {
      const res = await ApiClient.addTicketComment(
        selectedTicket.id,
        newComment.trim() || '(Csatolt fájlok / képernyőkép)',
        commentAttachments
      );
      if (res.ok) {
        setNewComment('');
        setCommentAttachments([]);
        // Refresh comments
        const detailRes = await ApiClient.getTicket(selectedTicket.id);
        if (detailRes.ok && detailRes.data) {
          setComments(detailRes.data.comments || []);
          if (detailRes.data.ticket) {
            setSelectedTicket(detailRes.data.ticket);
          }
        }
        fetchTickets();
      } else {
        alert(res.error || 'Hiba a hozzászólás küldésekor');
      }
    } catch (e: any) {
      alert('Hiba: ' + e.message);
    } finally {
      setSendingComment(false);
    }
  };

  const handleUpdateStatus = async (newStatus: TicketItem['status']) => {
    if (!selectedTicket) return;
    try {
      await ApiClient.updateTicket(selectedTicket.id, { status: newStatus });
      setSelectedTicket({ ...selectedTicket, status: newStatus });
      setTickets(prev => prev.map(t => t.id === selectedTicket.id ? { ...t, status: newStatus } : t));
    } catch (e: any) {
      alert('Hiba az állapot frissítésekor: ' + e.message);
    }
  };

  const filteredTickets = tickets.filter(t => {
    if (!search) return true;
    const q = search.toLowerCase();
    return t.title.toLowerCase().includes(q) || t.description.toLowerCase().includes(q);
  });

  const getStatusBadge = (s: TicketItem['status']) => {
    switch (s) {
      case 'open':
        return <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-amber-100 text-amber-800 dark:bg-amber-950/50 dark:text-amber-300">Nyitott</span>;
      case 'in_progress':
        return <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-blue-100 text-blue-800 dark:bg-blue-950/50 dark:text-blue-300">Folyamatban</span>;
      case 'waiting_user':
        return <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-purple-100 text-purple-800 dark:bg-purple-950/50 dark:text-purple-300">Válaszra vár</span>;
      case 'resolved':
        return <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-emerald-100 text-emerald-800 dark:bg-emerald-950/50 dark:text-emerald-300">Megoldva</span>;
      case 'closed':
        return <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-slate-200 text-slate-700 dark:bg-slate-800 dark:text-slate-400">Lezárva</span>;
    }
  };

  const getPriorityBadge = (p: TicketItem['priority']) => {
    switch (p) {
      case 'urgent':
        return <span className="px-2 py-0.5 rounded-md text-[10px] font-bold bg-rose-500 text-white animate-pulse">Sürgős</span>;
      case 'high':
        return <span className="px-2 py-0.5 rounded-md text-[10px] font-bold bg-rose-100 text-rose-800 dark:bg-rose-950/50 dark:text-rose-300">Magas</span>;
      case 'normal':
        return <span className="px-2 py-0.5 rounded-md text-[10px] font-semibold bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300">Normál</span>;
      case 'low':
        return <span className="px-2 py-0.5 rounded-md text-[10px] font-semibold bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-400">Alacsony</span>;
    }
  };

  const getCategoryBadge = (c: TicketItem['category']) => {
    switch (c) {
      case 'security_nis2':
        return <span className="px-2 py-0.5 rounded-md text-[10px] font-bold bg-purple-100 text-purple-800 dark:bg-purple-950/50 dark:text-purple-300 flex items-center gap-1"><ShieldAlert className="w-3 h-3" /> NIS2 Incidens</span>;
      case 'compliance':
        return <span className="px-2 py-0.5 rounded-md text-[10px] font-bold bg-indigo-100 text-indigo-800 dark:bg-indigo-950/50 dark:text-indigo-300">Megfelelőség</span>;
      case 'bug':
        return <span className="px-2 py-0.5 rounded-md text-[10px] font-semibold bg-amber-100 text-amber-800 dark:bg-amber-950/50 dark:text-amber-300">Rendszerhiba</span>;
      case 'data':
        return <span className="px-2 py-0.5 rounded-md text-[10px] font-semibold bg-teal-100 text-teal-800 dark:bg-teal-950/50 dark:text-teal-300">Adathiba</span>;
      case 'feature':
        return <span className="px-2 py-0.5 rounded-md text-[10px] font-semibold bg-blue-100 text-blue-800 dark:bg-blue-950/50 dark:text-blue-300">Fejlesztés</span>;
      default:
        return <span className="px-2 py-0.5 rounded-md text-[10px] font-semibold bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400">Egyéb</span>;
    }
  };

  // Render attachment chips in composer
  const renderAttachmentChips = (
    list: TicketAttachment[],
    onRemove: (idx: number) => void
  ) => {
    if (list.length === 0) return null;
    return (
      <div className="flex flex-wrap gap-2 pt-1">
        {list.map((att, idx) => {
          const isImg = att.type?.startsWith('image/') || /\.(png|jpe?g|webp|gif)$/i.test(att.name);
          return (
            <div
              key={idx}
              className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-xs text-slate-700 dark:text-slate-200 shadow-2xs group"
            >
              {isImg ? (
                <ImageIcon className="w-3.5 h-3.5 text-blue-500 shrink-0" />
              ) : (
                <FileText className="w-3.5 h-3.5 text-amber-500 shrink-0" />
              )}
              <span className="font-medium truncate max-w-[160px]" title={att.name}>
                {att.name}
              </span>
              <span className="text-[10px] text-slate-400">({formatBytes(att.size)})</span>
              <button
                type="button"
                onClick={() => onRemove(idx)}
                className="text-slate-400 hover:text-rose-500 ml-1 p-0.5 rounded transition"
                title="Melléklet eltávolítása"
              >
                <Trash2 className="w-3 h-3" />
              </button>
            </div>
          );
        })}
      </div>
    );
  };

  // Render attachments viewer in ticket or comments
  const renderAttachmentList = (attachments: any) => {
    const list = parseAttachments(attachments);
    if (list.length === 0) return null;

    return (
      <div className="pt-2">
        <div className="flex items-center gap-1 text-[11px] font-bold text-slate-500 dark:text-slate-400 mb-1.5">
          <Paperclip className="w-3 h-3" />
          <span>Csatolt fájlok / Képernyőképek ({list.length})</span>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {list.map((att, idx) => {
            const isImg = att.type?.startsWith('image/') || /\.(png|jpe?g|webp|gif)$/i.test(att.name);
            const rawUrl = att.url || `/api/files/${encodeURIComponent(att.key)}`;
            const displayUrl = ApiClient.getAuthFileUrl(rawUrl, false);

            return (
              <div
                key={idx}
                className="flex items-center justify-between gap-2 p-2 rounded-xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700/80 shadow-2xs hover:border-amber-500/50 transition group"
              >
                <div className="flex items-center gap-2 min-w-0">
                  {isImg ? (
                    <div
                      onClick={() => setPreviewImage({ url: displayUrl, name: att.name })}
                      className="w-10 h-10 rounded-lg overflow-hidden bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 shrink-0 cursor-pointer relative group/thumb"
                    >
                      <img
                        src={displayUrl}
                        alt={att.name}
                        className="w-full h-full object-cover"
                        loading="lazy"
                        onError={(e) => {
                          // Ha a kép nem tölthető be hibamentesen, fallback ikon
                          e.currentTarget.style.display = 'none';
                        }}
                      />
                      <div className="absolute inset-0 bg-slate-950/40 opacity-0 group-hover/thumb:opacity-100 flex items-center justify-center transition">
                        <Eye className="w-3.5 h-3.5 text-white" />
                      </div>
                    </div>
                  ) : (
                    <div className="w-10 h-10 rounded-lg bg-amber-50 dark:bg-amber-950/50 text-amber-600 dark:text-amber-400 border border-amber-200 dark:border-amber-800/60 flex items-center justify-center shrink-0">
                      <FileText className="w-5 h-5" />
                    </div>
                  )}

                  <div className="min-w-0">
                    <div
                      className="text-xs font-semibold text-slate-800 dark:text-slate-200 truncate cursor-pointer hover:text-amber-600 dark:hover:text-amber-400 transition"
                      title={att.name}
                      onClick={() => {
                        if (isImg) setPreviewImage({ url: displayUrl, name: att.name });
                        else ApiClient.downloadFile(rawUrl, att.name);
                      }}
                    >
                      {att.name}
                    </div>
                    <div className="text-[10px] text-slate-400 font-mono">
                      {formatBytes(att.size)}
                    </div>
                  </div>
                </div>

                <div className="flex items-center gap-1 shrink-0">
                  {isImg && (
                    <button
                      type="button"
                      onClick={() => setPreviewImage({ url: displayUrl, name: att.name })}
                      className="p-1.5 text-slate-400 hover:text-blue-600 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg transition"
                      title="Nagyítás"
                    >
                      <Eye className="w-3.5 h-3.5" />
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => ApiClient.downloadFile(rawUrl, att.name)}
                    className="p-1.5 text-slate-400 hover:text-amber-600 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg transition cursor-pointer"
                    title="Letöltés"
                  >
                    <Download className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Fejléc */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 bg-white dark:bg-slate-900 p-6 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-xs">
        <div className="flex items-center gap-3">
          <div className="p-3 bg-amber-500/10 text-amber-600 dark:text-amber-400 rounded-xl border border-amber-500/20">
            <LifeBuoy className="w-6 h-6" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-slate-900 dark:text-white flex items-center gap-2">
              <span>Hibajegyek & NIS2 Bejelentések</span>
              <span className="text-xs px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 dark:bg-amber-950/50 dark:text-amber-300 font-mono">
                {tickets.length} db
              </span>
            </h1>
            <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
              Rendszerhibák, adategyeztetések, módosítási kérések és NIS2 incidensbejelentések képernyőképekkel és csatolmányokkal
            </p>
          </div>
        </div>

        <button
          onClick={() => {
            setNewAttachments([]);
            setIsNewModalOpen(true);
          }}
          className="flex items-center gap-2 px-4 py-2.5 bg-amber-500 hover:bg-amber-600 text-white rounded-xl text-xs font-semibold shadow-md transition cursor-pointer shrink-0"
        >
          <Plus className="w-4 h-4" />
          <span>Új hibajegy feladása</span>
        </button>
      </div>

      {/* Szűrősáv */}
      <div className="flex flex-wrap items-center justify-between gap-3 bg-white dark:bg-slate-900 p-3 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-xs">
        <div className="flex items-center gap-2 flex-1 min-w-[220px]">
          <Search className="w-4 h-4 text-slate-400 shrink-0" />
          <input
            type="text"
            placeholder="Keresés cím vagy leírás alapján..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full bg-transparent text-xs text-slate-800 dark:text-slate-200 placeholder-slate-400 focus:outline-none"
          />
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          <div className="flex items-center gap-1 text-xs">
            <Filter className="w-3.5 h-3.5 text-slate-400" />
            <span className="text-slate-500 dark:text-slate-400 text-[11px]">Állapot:</span>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-2.5 py-1 text-xs rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 text-slate-800 dark:text-slate-200 focus:outline-none"
            >
              <option value="all">Mindegyik</option>
              <option value="open">Nyitott</option>
              <option value="in_progress">Folyamatban</option>
              <option value="waiting_user">Válaszra vár</option>
              <option value="resolved">Megoldva</option>
              <option value="closed">Lezárva</option>
            </select>
          </div>

          <div className="flex items-center gap-1 text-xs">
            <span className="text-slate-500 dark:text-slate-400 text-[11px]">Prioritás:</span>
            <select
              value={priorityFilter}
              onChange={(e) => setPriorityFilter(e.target.value)}
              className="px-2.5 py-1 text-xs rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 text-slate-800 dark:text-slate-200 focus:outline-none"
            >
              <option value="all">Mindegyik</option>
              <option value="urgent">Sürgős</option>
              <option value="high">Magas</option>
              <option value="normal">Normál</option>
              <option value="low">Alacsony</option>
            </select>
          </div>

          <button
            onClick={fetchTickets}
            className="p-1.5 rounded-lg border border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 transition"
            title="Frissítés"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {/* Jegyek listája */}
      {loading ? (
        <div className="p-12 text-center text-slate-400 text-xs">
          <RefreshCw className="w-6 h-6 animate-spin mx-auto mb-2 text-amber-500" />
          <span>Hibajegyek betöltése folyamatban...</span>
        </div>
      ) : filteredTickets.length === 0 ? (
        <div className="p-12 text-center bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 space-y-3">
          <LifeBuoy className="w-10 h-10 text-slate-300 dark:text-slate-700 mx-auto" />
          <p className="text-sm font-semibold text-slate-700 dark:text-slate-300">Nincsenek megjeleníthető hibajegyek</p>
          <p className="text-xs text-slate-400">Nem található a keresési és szűrési feltételeknek megfelelő bejelentés.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {filteredTickets.map((t) => {
            const atts = parseAttachments(t.attachments);
            return (
              <div
                key={t.id}
                onClick={() => handleOpenDetail(t)}
                className="bg-white dark:bg-slate-900 hover:bg-slate-50/80 dark:hover:bg-slate-800/50 p-4 rounded-2xl border border-slate-200 dark:border-slate-800 transition flex flex-col sm:flex-row sm:items-center justify-between gap-3 cursor-pointer shadow-xs"
              >
                <div className="flex-1 min-w-0 space-y-1.5">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-mono text-xs text-slate-400">#{t.id}</span>
                    {getStatusBadge(t.status)}
                    {getPriorityBadge(t.priority)}
                    {getCategoryBadge(t.category)}
                    <span className="text-[11px] text-slate-400 flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      {new Date(t.created_at).toLocaleDateString('hu-HU')}
                    </span>
                    {atts.length > 0 && (
                      <span className="text-[11px] text-amber-600 dark:text-amber-400 flex items-center gap-1 font-mono">
                        <Paperclip className="w-3 h-3" />
                        {atts.length} fájl
                      </span>
                    )}
                  </div>

                  <h3 className="text-sm font-bold text-slate-900 dark:text-white truncate">
                    {t.title}
                  </h3>
                  <p className="text-xs text-slate-500 dark:text-slate-400 line-clamp-1">
                    {t.description}
                  </p>
                </div>

                <div className="flex items-center gap-3 shrink-0 self-end sm:self-center">
                  <span className="flex items-center gap-1 text-xs text-slate-400">
                    <MessageSquare className="w-3.5 h-3.5" />
                    <span>{t.comment_count || 0}</span>
                  </span>
                  <ChevronRight className="w-4 h-4 text-slate-400" />
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Új hibajegy modal */}
      {isNewModalOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-4 bg-slate-900/60 backdrop-blur-xs overflow-y-auto"
          onPaste={(e) => handlePasteCapture(e, setUploadingAttachment, (att) => setNewAttachments(prev => [...prev, att]))}
        >
          <div className="bg-white dark:bg-slate-900 rounded-2xl max-w-xl w-full p-5 sm:p-6 shadow-2xl border border-slate-200 dark:border-slate-800 animate-in fade-in zoom-in-95 duration-150 my-auto max-h-[90vh] flex flex-col">
            <div className="flex items-center justify-between pb-3 border-b border-slate-100 dark:border-slate-800 shrink-0">
              <div className="flex items-center gap-2">
                <LifeBuoy className="w-5 h-5 text-amber-500" />
                <h3 className="text-sm font-bold text-slate-800 dark:text-slate-100">Új hibajegy / bejelentés feladása</h3>
              </div>
              <button
                type="button"
                onClick={() => setIsNewModalOpen(false)}
                className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 p-1 rounded-lg"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <form onSubmit={handleCreateTicket} className="mt-4 space-y-3.5 overflow-y-auto pr-1 flex-1">
              <div>
                <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300 mb-1">
                  Bejelentés címe / tárgya <span className="text-rose-500">*</span>
                </label>
                <input
                  type="text"
                  required
                  placeholder="pl. Hibás jogszabályi hivatkozás az 1.1 alpontban"
                  value={newTitle}
                  onChange={(e) => setNewTitle(e.target.value)}
                  className="w-full px-3 py-2 text-xs rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100 focus:ring-2 focus:ring-amber-500 outline-none"
                />
              </div>

              <div className="grid grid-cols-2 gap-2.5">
                <div>
                  <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300 mb-1">
                    Kategória
                  </label>
                  <select
                    value={newCategory}
                    onChange={(e) => setNewCategory(e.target.value as any)}
                    className="w-full px-3 py-2 text-xs rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100 focus:ring-2 focus:ring-amber-500 outline-none"
                  >
                    <option value="bug">Rendszerhiba (Bug)</option>
                    <option value="data">Adathiba / Eltérés</option>
                    <option value="compliance">Jogszabályi megfelelőség</option>
                    <option value="security_nis2">NIS2 Kiberbiztonsági incidens</option>
                    <option value="feature">Fejlesztési kérés</option>
                    <option value="other">Egyéb észrevétel</option>
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300 mb-1">
                    Sürgősség / Prioritás
                  </label>
                  <select
                    value={newPriority}
                    onChange={(e) => setNewPriority(e.target.value as any)}
                    className="w-full px-3 py-2 text-xs rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100 focus:ring-2 focus:ring-amber-500 outline-none"
                  >
                    <option value="low">Alacsony</option>
                    <option value="normal">Normál</option>
                    <option value="high">Magas</option>
                    <option value="urgent">Sürgős (Azonnali intézkedés)</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300 mb-1">
                  Részletes leírás <span className="text-rose-500">*</span>
                </label>
                <textarea
                  required
                  rows={4}
                  placeholder="Kérjük, írja le részletesen az észlelt problémát, az érintett menüpontot vagy rekordot..."
                  value={newDesc}
                  onChange={(e) => setNewDesc(e.target.value)}
                  className="w-full px-3 py-2 text-xs rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100 focus:ring-2 focus:ring-amber-500 outline-none"
                />
              </div>

              {/* Mellékletek & Képernyőképek beillesztése */}
              <div className="p-3.5 rounded-xl border border-dashed border-slate-200 dark:border-slate-700 bg-slate-50/50 dark:bg-slate-950/40 space-y-2">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-1.5 text-xs font-semibold text-slate-700 dark:text-slate-300">
                    <Paperclip className="w-4 h-4 text-amber-500" />
                    <span>Mellékletek & Képernyőképek</span>
                  </div>

                  <button
                    type="button"
                    onClick={() => newFileInputRef.current?.click()}
                    disabled={uploadingAttachment}
                    className="flex items-center gap-1.5 px-2.5 py-1 text-xs rounded-lg bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-300 hover:border-amber-500 transition shadow-2xs cursor-pointer"
                  >
                    <Plus className="w-3.5 h-3.5" />
                    <span>Fájl tallózása</span>
                  </button>
                  <input
                    type="file"
                    ref={newFileInputRef}
                    multiple
                    accept="image/*,.pdf,.docx,.xlsx,.txt"
                    className="hidden"
                    onChange={(e) => {
                      if (e.target.files) {
                        uploadFiles(Array.from(e.target.files), setUploadingAttachment, (att) =>
                          setNewAttachments(prev => [...prev, att])
                        );
                        e.target.value = '';
                      }
                    }}
                  />
                </div>

                <p className="text-[11px] text-slate-500 dark:text-slate-400">
                  💡 <span className="font-semibold text-amber-600 dark:text-amber-400">Tipp:</span> Képernyőképet közvetlenül beilleszthet a vágólapról (<kbd className="px-1 py-0.5 bg-slate-200 dark:bg-slate-800 rounded font-mono text-[10px]">Ctrl+V</kbd>), vagy húzzon ide fájlokat (PNG, JPG, PDF, DOCX, XLSX, max 15MB).
                </p>

                {uploadingAttachment && (
                  <div className="flex items-center gap-2 text-xs text-amber-600 dark:text-amber-400 py-1">
                    <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                    <span>Képernyőkép / fájl feltöltése folyamatban...</span>
                  </div>
                )}

                {renderAttachmentChips(newAttachments, (idx) =>
                  setNewAttachments(prev => prev.filter((_, i) => i !== idx))
                )}
              </div>

              <div className="flex items-center justify-end gap-2 pt-3 border-t border-slate-200 dark:border-slate-800">
                <button
                  type="button"
                  onClick={() => setIsNewModalOpen(false)}
                  className="px-4 py-2 rounded-xl text-xs font-medium text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 transition"
                >
                  Mégse
                </button>
                <button
                  type="submit"
                  disabled={creating || uploadingAttachment}
                  className="px-5 py-2 rounded-xl bg-amber-500 hover:bg-amber-600 disabled:opacity-50 text-white text-xs font-semibold shadow-md transition cursor-pointer"
                >
                  {creating ? 'Beküldés...' : 'Hibajegy beküldése'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Részletek & Hozzászólások Drawer / Modal */}
      {selectedTicket && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-4 bg-slate-900/60 backdrop-blur-xs overflow-y-auto"
          onPaste={(e) => handlePasteCapture(e, setUploadingCommentAttachment, (att) => setCommentAttachments(prev => [...prev, att]))}
        >
          <div className="bg-white dark:bg-slate-900 rounded-2xl max-w-2xl w-full p-5 sm:p-6 shadow-2xl border border-slate-200 dark:border-slate-800 my-auto max-h-[90vh] flex flex-col animate-in fade-in zoom-in-95 duration-150">
            <div className="flex items-center justify-between pb-3 border-b border-slate-100 dark:border-slate-800 shrink-0">
              <div className="flex items-center gap-2">
                <span className="font-mono text-xs text-slate-400">#{selectedTicket.id}</span>
                <h3 className="text-sm font-bold text-slate-800 dark:text-slate-100 truncate max-w-md">
                  {selectedTicket.title}
                </h3>
              </div>
              <button
                type="button"
                onClick={() => setSelectedTicket(null)}
                className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 p-1 rounded-lg"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto py-4 space-y-4 pr-1">
              {/* Metaadat sáv */}
              <div className="flex flex-wrap items-center justify-between gap-2 p-3 rounded-xl bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 text-xs">
                <div className="flex items-center gap-2">
                  <span className="text-slate-400 text-[11px]">Állapot módosítása:</span>
                  <select
                    value={selectedTicket.status}
                    onChange={(e) => handleUpdateStatus(e.target.value as any)}
                    className="px-2 py-0.5 rounded-lg border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-xs font-semibold focus:outline-none"
                  >
                    <option value="open">Nyitott</option>
                    <option value="in_progress">Folyamatban</option>
                    <option value="waiting_user">Válaszra vár</option>
                    <option value="resolved">Megoldva</option>
                    <option value="closed">Lezárva</option>
                  </select>
                </div>
                <div className="flex items-center gap-1.5">
                  {getPriorityBadge(selectedTicket.priority)}
                  {getCategoryBadge(selectedTicket.category)}
                </div>
              </div>

              {/* Leírás */}
              <div className="space-y-2">
                <span className="text-[11px] font-bold uppercase tracking-wider text-slate-400">Leírás</span>
                <div className="p-3.5 rounded-xl bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 text-xs text-slate-800 dark:text-slate-200 whitespace-pre-wrap leading-relaxed">
                  {selectedTicket.description}
                </div>

                {/* Hibajegyhez tartozó kezdeti csatolmányok */}
                {renderAttachmentList(selectedTicket.attachments)}

                <div className="text-[10px] text-slate-400 pt-1">
                  Beküldő: {selectedTicket.user_name || selectedTicket.user_email || 'Ismeretlen'} • {new Date(selectedTicket.created_at).toLocaleString('hu-HU')}
                </div>
              </div>

              {/* Hozzászólások idővonala */}
              <div className="space-y-3 pt-2">
                <span className="text-[11px] font-bold uppercase tracking-wider text-slate-400 flex items-center gap-1.5">
                  <MessageSquare className="w-3.5 h-3.5" />
                  <span>Kommentek & Teendők ({comments.length})</span>
                </span>

                {loadingDetail ? (
                  <div className="text-center py-4 text-xs text-slate-400">Betöltés...</div>
                ) : comments.length === 0 ? (
                  <div className="text-center py-6 text-xs text-slate-400 border border-dashed border-slate-200 dark:border-slate-800 rounded-xl">
                    Még nincsenek hozzászólások ehhez a jegyhez.
                  </div>
                ) : (
                  <div className="space-y-2.5">
                    {comments.map((c) => (
                      <div
                        key={c.id}
                        className="p-3.5 rounded-xl bg-white dark:bg-slate-800/80 border border-slate-200 dark:border-slate-700 text-xs space-y-1.5 shadow-2xs"
                      >
                        <div className="flex items-center justify-between text-[10px] text-slate-400">
                          <span className="font-bold text-slate-700 dark:text-slate-300">
                            {c.user_name || 'Felhasználó'} ({c.user_role || 'munkatárs'})
                          </span>
                          <span>{new Date(c.created_at).toLocaleString('hu-HU')}</span>
                        </div>
                        <p className="text-slate-800 dark:text-slate-200 whitespace-pre-wrap">{c.comment}</p>
                        {renderAttachmentList(c.attachments)}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Új hozzászólás űrlap csatolmány támogatással */}
            <div className="pt-3 border-t border-slate-100 dark:border-slate-800 shrink-0 space-y-2">
              {renderAttachmentChips(commentAttachments, (idx) =>
                setCommentAttachments(prev => prev.filter((_, i) => i !== idx))
              )}

              {uploadingCommentAttachment && (
                <div className="flex items-center gap-2 text-xs text-amber-600 dark:text-amber-400 py-0.5">
                  <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                  <span>Melléklet / képernyőkép feltöltése...</span>
                </div>
              )}

              <form onSubmit={handleAddComment} className="flex items-center gap-2">
                <input
                  type="file"
                  ref={commentFileInputRef}
                  multiple
                  accept="image/*,.pdf,.docx,.xlsx,.txt"
                  className="hidden"
                  onChange={(e) => {
                    if (e.target.files) {
                      uploadFiles(Array.from(e.target.files), setUploadingCommentAttachment, (att) =>
                        setCommentAttachments(prev => [...prev, att])
                      );
                      e.target.value = '';
                    }
                  }}
                />

                <button
                  type="button"
                  onClick={() => commentFileInputRef.current?.click()}
                  className="p-2 rounded-xl border border-slate-200 dark:border-slate-700 hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-500 hover:text-amber-600 transition"
                  title="Fájl csatolása vagy kép tallózása"
                >
                  <Paperclip className="w-4 h-4" />
                </button>

                <input
                  type="text"
                  placeholder="Válasz írása... (vagy nyomjon Ctrl+V-t képernyőképhez!)"
                  value={newComment}
                  onChange={(e) => setNewComment(e.target.value)}
                  className="flex-1 px-3.5 py-2 text-xs rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-950 text-slate-800 dark:text-slate-200 focus:outline-none focus:ring-2 focus:ring-amber-500"
                />

                <button
                  type="submit"
                  disabled={sendingComment || uploadingCommentAttachment || (!newComment.trim() && commentAttachments.length === 0)}
                  className="px-4 py-2 bg-amber-500 hover:bg-amber-600 disabled:opacity-50 text-white text-xs font-semibold rounded-xl flex items-center gap-1.5 transition cursor-pointer"
                >
                  <Send className="w-3.5 h-3.5" />
                  <span>Küldés</span>
                </button>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Nagyítható Képernyőkép / Kép Lightbox Modal */}
      {previewImage && (
        <div
          className="fixed inset-0 z-70 flex items-center justify-center p-3 sm:p-4 bg-slate-950/80 backdrop-blur-sm animate-in fade-in duration-150 overflow-y-auto"
          onClick={() => setPreviewImage(null)}
        >
          <div
            className="relative max-w-4xl my-auto max-h-[90vh] bg-white dark:bg-slate-900 rounded-2xl overflow-hidden shadow-2xl border border-slate-200 dark:border-slate-800 flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-4 py-2.5 border-b border-slate-100 dark:border-slate-800 bg-slate-50 dark:bg-slate-950 text-xs shrink-0">
              <span className="font-semibold text-slate-800 dark:text-slate-200 truncate max-w-md">
                {previewImage.name}
              </span>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => ApiClient.downloadFile(previewImage.url, previewImage.name)}
                  className="flex items-center gap-1 px-2.5 py-1 bg-amber-500 hover:bg-amber-600 text-white text-xs font-medium rounded-lg transition cursor-pointer"
                >
                  <Download className="w-3.5 h-3.5" />
                  <span>Letöltés</span>
                </button>
                <button
                  type="button"
                  onClick={() => setPreviewImage(null)}
                  className="p-1 rounded-lg text-slate-400 hover:text-slate-600 dark:hover:text-slate-200"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>

            <div className="p-2 overflow-auto flex items-center justify-center bg-slate-950/90">
              <img
                src={previewImage.url}
                alt={previewImage.name}
                className="max-h-[80vh] w-auto object-contain rounded-lg"
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
